import {
    AccountCategory,
    type CategorySummary,
    type HolisticSummary,
    type HolisticSummaryCategory,
} from '@fruitful/types';
import { router } from 'expo-router';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useRef, useState } from 'react';
import type { LayoutChangeEvent, View as RNView } from 'react-native';
import {
    ArrowRightIcon,
    BodyLargeRegular,
    BodySmallEmphasized,
    CircleAlertIcon,
    Icon,
    LabelRegular,
    LoadingSkeleton,
    Private,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import { isAccountInErrorState } from '../../../common/utils/holisticUtils';

const ORDERED_CATEGORIES: HolisticSummaryCategory[] = [
    AccountCategory.SPEND,
    AccountCategory.SAVE,
    AccountCategory.INVEST,
    AccountCategory.BORROW,
];

interface CategoryCardProps {
    width: number;
    isRefetching: boolean;
    accountCategory: AccountCategory;
    categorySummary: CategorySummary;
    onPress: () => void;
}

const CategoryCard = ({
    width,
    isRefetching,
    accountCategory,
    categorySummary,
    onPress,
}: CategoryCardProps) => {
    const numberOfAccounts = categorySummary.accounts.length;

    const doAccountsHaveErrors = categorySummary.accounts.some(
        isAccountInErrorState,
    );

    const isBorrowCategory = accountCategory === AccountCategory.BORROW;
    const accountBalanceLoading =
        isRefetching ||
        categorySummary.totalCurrentBalanceCents === null ||
        categorySummary.totalCurrentBalanceCents === undefined;

    const accountCountLabel =
        numberOfAccounts === 1
            ? isBorrowCategory
                ? '1 loan'
                : '1 account'
            : isBorrowCategory
              ? `${numberOfAccounts} loans`
              : `${numberOfAccounts} accounts`;

    return (
        <YStack
            gap={16}
            backgroundColor="$white"
            justifyContent="space-between"
            borderRadius={12}
            width={width}
            padding={16}
            cursor="pointer"
            onPress={onPress}
            opacity={width === 0 ? 0 : 1} // first render might be 0 until layout event
            animation="quick"
            animateOnly={['opacity']}
        >
            <XStack
                width="100%"
                alignItems="center"
                justifyContent="space-between"
            >
                <BodySmallEmphasized>
                    {capitalize(accountCategory)}
                </BodySmallEmphasized>
                <XStack alignItems="center" gap={4}>
                    {doAccountsHaveErrors && (
                        <Icon
                            icon={CircleAlertIcon}
                            size={12}
                            color="$red1000"
                        />
                    )}
                    <Icon icon={ArrowRightIcon} size={16} color="$black" />
                </XStack>
            </XStack>

            <YStack gap={4}>
                <Private>
                    {accountBalanceLoading ? (
                        <LoadingSkeleton
                            paddingVertical={4}
                            height={28}
                            width={isBorrowCategory ? 30 : 120}
                        />
                    ) : (
                        <BodyLargeRegular>
                            {isBorrowCategory
                                ? numberOfAccounts
                                : formatDollars(
                                      (categorySummary.totalCurrentBalanceCents ??
                                          0) / 100,
                                  )}
                        </BodyLargeRegular>
                    )}
                </Private>
                <LabelRegular color="$gray1000">
                    {accountCountLabel}
                </LabelRegular>
            </YStack>
        </YStack>
    );
};

interface LinkedAccountsProps {
    isRefetchingHolisticSummary: boolean;
    holisticSummary: HolisticSummary;
}

const LinkedAccounts = ({
    isRefetchingHolisticSummary,
    holisticSummary,
}: LinkedAccountsProps) => {
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<RNView | null>(null);

    const onContainerLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(event.nativeEvent.layout.width);
    };

    const gap = 16;
    const cardWidth = containerWidth === 0 ? 0 : (containerWidth - gap) / 2;

    return (
        <YStack
            width="100%"
            alignItems="stretch"
            gap={gap}
            ref={containerRef}
            onLayout={onContainerLayout}
        >
            <XStack flexWrap="wrap" gap={gap}>
                {ORDERED_CATEGORIES.filter(
                    (accountCategory) =>
                        holisticSummary[accountCategory].accounts.length > 0,
                ).map((accountCategory) => {
                    const categorySummary = holisticSummary[accountCategory];

                    return (
                        <CategoryCard
                            key={accountCategory}
                            width={cardWidth}
                            isRefetching={isRefetchingHolisticSummary}
                            accountCategory={accountCategory}
                            categorySummary={categorySummary}
                            onPress={() => {
                                router.navigate({
                                    pathname: '/holistic/[category]',
                                    params: {
                                        category: accountCategory,
                                    },
                                });
                            }}
                        />
                    );
                })}
            </XStack>
        </YStack>
    );
};

export default LinkedAccounts;
